<template>
  <div v-if="isDataLoaded && isNoSelectedProject && !isNoDataFetch" class="schedule-container">
    <div class="chart-container">
      <!-- Hard Costs stacked bar -->
      <div class="title-container">
        <div class="chart-title">Schedule Overage Summary</div>
      </div>
      <div class="stacked-charts-container">
        <div class="chart hc-offenders-stacked-bar">
          <div class="chart-subtitle top-offenders-subtitle">
            Hard Costs
          </div>
          <Bar :height="350" :width="1550" :data="hardCostsStackedBarData" :options="hardCostsStackedBarOptions" />
        </div>
      </div>
    </div>

    <!-- Schedule Tracking Section -->
    <div class="schedule-tracking-header-container">
      <div class="schedule-tracking-header-title">Schedule Tracking</div>
      <div class="schedule-tracking-subtitle">
        Based on billing start (0%) to substantial completion (95%)
      </div>
    </div>

    <div class="grid-container schedule-gantt" id="scheduleViewGrid">
      <div class="schedule-view-toggle">
        <button 
          :class="{ 'active': scheduleViewMode === 'projected' }" 
          @click="scheduleViewMode = 'projected'"
        >
          Projected Schedule
        </button>
        <button 
          :class="{ 'active': scheduleViewMode === 'billing' }" 
          @click="scheduleViewMode = 'billing'"
        >
          Billing-Based Schedule
        </button>
      </div>

      <ag-grid 
        :row-height="30" 
        :row-data="scheduleViewMode === 'billing' ? billingBasedScheduleData : dataShow" 
        :column-defs="scheduleViewMode === 'billing' ? billingBasedColumnDefs : localColumnDefs" 
        :column-options="columnOptions"
        :groupDefaultExpanded="2"
        :tree-data="true" 
        :full-scroll="true" 
        custom-column="buttonGantt" 
        :key="scheduleViewMode + (scheduleViewMode === 'billing' ? billingBasedScheduleData.length : dataShow.length)" 
        @setChartData="setChartData"
      />

      <ToolTip :is-show="tooltipIsShow" :top="`${tooltipTop}px`" :left="`${tooltipLeft}px`" :html="tooltipHtml" />
    </div>
  </div>
  <div v-if="!isNoSelectedProject" class="no-selected-projects">No generated charts to show, please select a comp set.
  </div>
  <div v-if="isNoSelectedProject && isNoDataFetch" class="no-selected-projects">An error occurred. No data has been
    fetched.</div>
</template>
<script setup>
/* eslint-disable */
import { standardStackedBarChartJson } from "@/assets/js/chart_config";
import { axiosQuery, decimalPart, formatDecimals, numberFormat, percentFormat } from "@/assets/js/utils";
import { cleanupHierarchyNames } from "@/data/data";
import { useAuthStore } from "@/stores/auth_store";
import { useCompSetStore } from "@/stores/comp_set_store";
import { useErrorStore } from "@/stores/error_store";
import { useGanttTooltipStore } from "@/stores/gantt_tooltip_store";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Colors,
  Legend,
  LinearScale,
  LogarithmicScale,
  Title,
  Tooltip,
} from "chart.js";
import { computed, onMounted, reactive, ref } from "vue";
import { Bar } from "vue-chartjs"; // note this is a publicly available Chart JS wrapper, MIT licensed
import AgGrid from "@/components/AgGrid.vue";
import ToolTip from "@/components/helpers/ToolTip.vue";
import GanttCellRenderer from "@/components/renderers/GanttCellRenderer.vue";

let localColumnDefs = [
  {
    headerName: "Planned Duration",
    field: "projected_project_months",
    aggFunc: avgAggregate,
    headerClass: 'header-custom-lilac',
    editable: false,
    valueParser: "Number(newValue)",
    type: 'rightAligned'
  },
  {
    headerName: "Actual Duration",
    field: "actual_project_months",
    aggFunc: avgAggregate,
    headerClass: 'header-custom-lilac',
    editable: false,
    valueParser: "Number(newValue)",
    type: 'rightAligned'
  },
  {
    headerName: "Variance",
    field: "variance",
    aggFunc: varianceAggregate,
    headerClass: 'header-custom-lightblue',
    editable: false,
    valueParser: "Number(newValue)",
    type: 'rightAligned'
  },
]
const columnOptions = {
  actual_project_months: {
    minWidth: 110,
  },
  projected_project_months: {
    minWidth: 110,
  },
  // The default ag_grid_field_helper makes a percent, so fix that here
  variance: {
    minWidth: 100,
    cellRenderer: (params => {
      if (params.value !== 0 && !params.value) {
        return ''
      }
      return formatDecimals(params.value)
    })
  },
}
const maxMonths = 48
let columnGroup = {}
Array(maxMonths).fill(0).forEach((_, month) => {
  if (month % 12 === 0) {
    if (month > 0) {
      // save old column defs
      localColumnDefs.push(columnGroup)
    }
    columnGroup = {
      groupId: `year_${month / 12 + 1}`,
      headerName: `Year ${month / 12 + 1}`,
      menuTabs: ['columnsMenuTab'],
      children: []
    }
  }
  columnGroup.children.push({
    headerName: `${month % 12 + 1}`,
    field: `month_length_change_${month}`,
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      suppressCount: true,
      checkbox: true,
      // This innerRenderer is used by group items and then the params are as defined by cellRendererSelector
      // Without this innerRenderer here, the group items will not show the gantt chart
      innerRenderer: 'GanttCellRenderer',
      suppressDoubleClickExpand: true,
      suppressEnterExpand: true,
    },
    // This function does the aggregates for group rows. That array gets stored internally by AgGrid as params.value
    // and is used by cellRendererSelector
    aggFunc: (params => monthAggregate(params, month)),
    editable: false,
    // columnGroupShow: 'open', // Does not work unless we have a column that is open on the left...
    suppressHeaderMenuButton: true,
    sortable: false,
    suppressMovable: true,
    // this seems to help with the far right, though 20 allows us to see the 48th month better
    maxWidth: 34, // month > 45 ? 30 : 20,
    headerClass: month % 12 === 0 ? 'schedule-sparkline-header year' : 'schedule-sparkline-header',
    cellClass: month % 12 === 0 ? 'schedule-sparkline year' : 'schedule-sparkline',
    cellRendererSelector: (params) => {
      return {
        component: GanttCellRenderer,
        params: {
          topWidthPct: parseFloat(params.value[0]),
          bottomWidthPct: parseFloat(params.value[1]),
          isTopRightAligned: params.value[2],
          isBottomRightAligned: params.value[3],
          projectedProjectMonths: formatDecimals(parseFloat(params.value[4]), 1),
          actualProjectMonths: formatDecimals(parseFloat(params.value[5]), 1),
          // -0.00 sometimes crops up here...
          projectedMonthsIn: formatDecimals(Math.abs(parseFloat(params.value[6])), 1),
          actualMonthsIn: formatDecimals(Math.abs(parseFloat(params.value[7])), 1),
        },
      }
    },
    filter: false,
  })
})
// save last set of column Defs
localColumnDefs.push(columnGroup)

ChartJS.register(
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  Colors,
  ArcElement,
  LogarithmicScale
);
let isDataLoaded = computed(() => {
  return isAveragedDataLoaded.value
})
let isNoSelectedProject = ref(false);
let isNoDataFetch = ref(false);
const labelMaxLength = 15;
const hardCostsStackedBarData = computed(() => {
  return {
    labels: schedStackedBarLabels.value,
    datasets: [
      {
        label: "Projected",
        backgroundColor: "#4A5FD5", // $brightBlue1
        data: projectedProjectMonthsArr.value,
      },
      {
        label: "Sched Variance",
        backgroundColor: "#84CADE", // $lightBlue1
        data: schedVarianceArr.value,
      },
    ],
  };
});
let hardStackedBarJsonConfig = standardStackedBarChartJson({
  barPercentage: 0.5,
  xTicksFontSize: 12,
  xTicksFont: 'Inter',
  labelForcedLength: labelMaxLength,
})
const hardCostsStackedBarOptions = computed(() => {
  let json = hardStackedBarJsonConfig
  // override some configurations
  json.indexAxis = "y"
  json.scales.y = {
    stacked: true,
    display: true
  }
  json.scales.x.ticks = {
    font: "10",
    family: "Inter",
    stepSize: "1",
    beginAtZero: true,
    autoSkip: false,
    callback: function (label) {
      return label
    }
  }
  // See documentation for lineWidth. Decided not to use major ticks just for ease, but didn't try them out.
  // 1. https://www.chartjs.org/docs/latest/axes/styling.html
  // 2. https://www.chartjs.org/docs/latest/general/options.html#tick
  json.scales.x.grid = {
    color: '#eee',
    lineWidth: function (context) {
      const index = context.index;
      return (index > 0 && index % 12 === 0) ? '3' : '1'
    },
    drawOnChartArea: true,
  }
  json.scales.y.ticks = {
    font: {
      size: (tick) => (tick?.tick?.label.includes('Total') ? "14" : "10"),
      family: (tick) => (tick?.tick?.label.includes('Total') ? "Inter-Bold" : "Inter"),
    },
  }
  json.scales.y.grid = {
    drawOnChartArea: false
  }
  json.plugins.legend = {
    labels: {
      boxWidth: 10,
    },
    display: true,
    position: "top",
    align: "end"
  }
  json.responsive = true
  json.maintainAspectRatio = true
  json.plugins.tooltip = {}
  json.plugins.tooltip.callbacks = {
    label: function (context) {
      const label = context.dataset.label;
      const value = context.parsed.x;
      const index = context.dataIndex;
      let otherLabel = ''
      if (label === 'Projected') {
        otherLabel = `Sched Variance: ${formatDecimals(schedVarianceArr.value[index])} `
      } else {
        otherLabel = `Projected: ${formatDecimals(projectedProjectMonthsArr.value[index])} `
      }
      return label + ": " + formatDecimals(value) + ` (${otherLabel})`;
    },
    footer: function (context) {
      const index = context[0].dataIndex;
      let varianceLabel = ''
      varianceLabel = `Variance: ${percentFormat(schedVariancePercArr.value[index])}`
      return (
        varianceLabel
      );
    },
  }
  return json
})

const compSetStore = useCompSetStore()
compSetStore.$subscribe(syncProjects)
const currentProjects = ref(compSetStore.getIncludedProjects)
const selectedProjects = ref(compSetStore.selectedProjects);
function syncProjects() {
  currentProjects.value = compSetStore.getIncludedProjects
  // TODO this doesn't seem to be updating dynamically as of 6/30/2024
  selectedProjects.value = compSetStore.selectedProjects
  updateData()
}
const errorStore = useErrorStore();
let aggregateData = {};
let processedAggregateData = {};
let lastProjectsQuery = []
// Schedule view mode toggle
const scheduleViewMode = ref('projected'); // 'projected' or 'billing'

// Billing-based schedule data
const billingBasedScheduleData = ref([]);

// Billing-based column definitions
const billingBasedColumnDefs = [
  {
    headerName: "Planned Duration",
    field: "projected_project_months",
    aggFunc: avgAggregate,
    headerClass: 'header-custom-lilac',
    editable: false,
    valueParser: "Number(newValue)",
    type: 'rightAligned'
  },
  {
    headerName: "Billing Start",
    field: "billing_start_date",
    headerClass: 'header-custom-lilac',
    editable: false,
    valueFormatter: params => formatDate(params.value),
    type: 'leftAligned'
  },
  {
    headerName: "95% Complete",
    field: "billing_95_complete_date",
    headerClass: 'header-custom-lilac',
    editable: false,
    valueFormatter: params => formatDate(params.value),
    type: 'leftAligned'
  },
  {
    headerName: "Actual Duration",
    field: "actual_project_months",
    aggFunc: avgAggregate,
    headerClass: 'header-custom-lilac',
    editable: false,
    valueParser: "Number(newValue)",
    type: 'rightAligned'
  },
  {
    headerName: "Variance",
    field: "variance",
    aggFunc: varianceAggregate,
    headerClass: 'header-custom-lightblue',
    editable: false,
    valueParser: "Number(newValue)",
    type: 'rightAligned'
  },
];

// Add the month columns to billingBasedColumnDefs
Array(maxMonths).fill(0).forEach((_, month) => {
  if (month % 12 === 0) {
    if (month > 0) {
      // save old column defs
      billingBasedColumnDefs.push(columnGroup)
    }
    columnGroup = {
      groupId: `year_${month / 12 + 1}`,
      headerName: `Year ${month / 12 + 1}`,
      menuTabs: ['columnsMenuTab'],
      children: []
    }
  }
  columnGroup.children.push({
    headerName: `${month % 12 + 1}`,
    field: `month_length_change_${month}`,
    cellRenderer: 'agGroupCellRenderer',
    cellRendererParams: {
      suppressCount: true,
      checkbox: true,
      innerRenderer: 'GanttCellRenderer',
      suppressDoubleClickExpand: true,
      suppressEnterExpand: true,
    },
    aggFunc: (params => monthAggregate(params, month)),
    editable: false,
    suppressHeaderMenuButton: true,
    sortable: false,
    suppressMovable: true,
    maxWidth: 34,
    headerClass: month % 12 === 0 ? 'schedule-sparkline-header year' : 'schedule-sparkline-header',
    cellClass: month % 12 === 0 ? 'schedule-sparkline year' : 'schedule-sparkline',
    cellRendererSelector: (params) => {
      return {
        component: GanttCellRenderer,
        params: {
          topWidthPct: parseFloat(params.value[0]),
          bottomWidthPct: parseFloat(params.value[1]),
          isTopRightAligned: params.value[2],
          isBottomRightAligned: params.value[3],
          projectedProjectMonths: formatDecimals(parseFloat(params.value[4]), 1),
          actualProjectMonths: formatDecimals(parseFloat(params.value[5]), 1),
          projectedMonthsIn: formatDecimals(Math.abs(parseFloat(params.value[6])), 1),
          actualMonthsIn: formatDecimals(Math.abs(parseFloat(params.value[7])), 1),
        },
      }
    },
    filter: false,
  })
});
// save last set of column Defs
billingBasedColumnDefs.push(columnGroup);

// Format date function
function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString();
}

const dataShow = ref([]);
// this is fetching data for all cost types
// Process the data for the summed data
function fetchGridData() {
  const selectedIds = Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]).join(',')
  // Do *not* requery data
  if (lastProjectsQuery === selectedIds) {
    return
  } else {
    lastProjectsQuery = selectedIds
  } 

  axiosQuery(
    `${apiBase}scope_of_works/summed_dates?project_ids=${selectedIds}`,
    authToken,
    (data) => {
      if (data.length == 0) {
        isNoDataFetch.value = true
      } else {
        aggregateData = {}
        processedAggregateData = {}
        data?.forEach((row) => {
          if (row.itemHierarchy && row.actual_project_months) {
            let months = {}
            Array(maxMonths).fill(0).forEach((_, month) => {
              // TODO || parts here are to aid in testing. Need more projected start dates/months
              let projectedStartMonth = parseFloat(row.projected_months_into_project_start || row.actual_months_into_project_start)
              // TODO uncertain why some are < 0
              if (projectedStartMonth < 0) projectedStartMonth = 1
              let projectedEndMonth = projectedStartMonth + parseFloat(row.projected_project_months || row.actual_project_months)
              let projectedContribution = projectedStartMonth <= (month + 1) && projectedEndMonth >= month + 2 ? 1 : 0

              let actualStartMonth = parseFloat(row.actual_months_into_project_start)
              let actualEndMonth = actualStartMonth + parseFloat(row.actual_project_months)
              let actualContribution = actualStartMonth <= (month + 1) && actualEndMonth >= month + 2 ? 1 : 0

              // Get percentage into month -- divided into 10 pieces
              if (parseFloat(numberFormat(projectedStartMonth, 0)) === month) {
                projectedContribution = decimalPart(projectedStartMonth)
                // months[`month_length_change_${month}_projected_start`] = true
              }
              if (parseFloat(numberFormat(actualStartMonth, 0)) === month) {
                actualContribution = decimalPart(actualStartMonth)
                // months[`month_length_change_${month}_actual_start`] = true
              }
              // If we are on the last month....
              if (parseFloat(numberFormat(projectedContribution, 0)) === month + 1) {
                projectedContribution = decimalPart(projectedContribution)
              }
              if (parseFloat(numberFormat(actualContribution, 0)) === month + 1) {
                actualContribution = decimalPart(actualContribution)
              }
              months[`month_length_change_${month}`] = [projectedContribution, actualContribution]
            })

            cleanupHierarchyNames(row.itemHierarchy).forEach(term => {
              aggregateData[term] ||= []
              aggregateData[term].push({
                projected_project_months: numberFormat(row.projected_project_months),
                actual_project_months: numberFormat(row.actual_project_months),
                projected_months_into_project_start: numberFormat(row.projected_months_into_project_start),
                actual_months_into_project_start: numberFormat(row.actual_months_into_project_start),
                variance: numberFormat((row.actual_project_months || 0) - (row.projected_project_months || 0)),
                ...months
              })
            })
          }
        });

        // Process the aggregate data for the subtotal/total rows in the itemHierarchy
        processRollupData()
        // Process the averaged data for the leaf nodes
        processLeafNodeData(data)
        isNoDataFetch.value = false
      }

      isNoSelectedProject.value = true
    },
    errorStore
  );
}

// The goal is to store in the rolled up terms:
// 1. minimum months into project start, as we want to see the earliest it is projected to or actually started
// 2. maximum length of project. This needs to be the latest the project could/did end minus the earliest it could/did start
function processRollupData() {
  Object.keys(aggregateData).forEach(term => {
    let minActualMonthsIntoProject = 0.0;
    let minProjectedMonthsIntoProject = 0.0;
    let maxActualProjectMonthsLength = 0.0;
    let maxProjectedProjectMonthsLength = 0.0;

    if (aggregateData[term]?.length) {
      aggregateData[term].forEach(value => {
        if (minActualMonthsIntoProject) {
          minActualMonthsIntoProject = Math.min(minActualMonthsIntoProject, parseFloat(value.actual_months_into_project_start || 0.0))
        } else {
          minActualMonthsIntoProject = parseFloat(value.actual_months_into_project_start || 0.0)
        }
      })

      aggregateData[term].forEach(value => {
        if (minProjectedMonthsIntoProject) {
          minProjectedMonthsIntoProject = Math.min(minProjectedMonthsIntoProject, parseFloat(value.projected_months_into_project_start || 0.0))
        } else {
          minProjectedMonthsIntoProject = parseFloat(value.projected_months_into_project_start || 0.0)
        }
      })

      aggregateData[term].forEach(value => {
        const actualProjectLength = parseFloat(value.actual_months_into_project_start || 0.0) + parseFloat(value.actual_project_months || 0.0)
        if (maxActualProjectMonthsLength) {
          maxActualProjectMonthsLength = Math.max(maxActualProjectMonthsLength, actualProjectLength)
        } else {
          maxActualProjectMonthsLength = actualProjectLength
        }
      })
      maxActualProjectMonthsLength -= minActualMonthsIntoProject

      aggregateData[term].forEach(value => {
        const projectedProjectLength = parseFloat(value.projected_months_into_project_start || 0.0) + parseFloat(value.projected_project_months || 0.0)
        if (maxProjectedProjectMonthsLength) {
          maxProjectedProjectMonthsLength = Math.max(maxProjectedProjectMonthsLength, projectedProjectLength)
        } else {
          maxProjectedProjectMonthsLength = projectedProjectLength
        }
      })
      maxProjectedProjectMonthsLength -= minProjectedMonthsIntoProject

      // TODO minor hack for now to make data look good since we don't always have projected months
      if (!maxProjectedProjectMonthsLength) { maxProjectedProjectMonthsLength = maxActualProjectMonthsLength }
    }

    processedAggregateData[term] = {
      minActualMonthsIntoProject: minActualMonthsIntoProject,
      minProjectedMonthsIntoProject: minProjectedMonthsIntoProject,
      maxActualProjectMonthsLength: maxActualProjectMonthsLength,
      maxProjectedProjectMonthsLength: maxProjectedProjectMonthsLength
    }
  })
}

// Process the data for the individual nodes
let isAveragedDataLoaded = ref(false)
function processLeafNodeData(data) {
  data?.forEach((row) => {
    if (row.itemHierarchy && row.actual_project_months) {
      let months = {}
      Array(maxMonths).fill(0).forEach((_, month) => {
        // TODO || parts here are to aid in testing. Need more projected start dates/months
        let projectedStartMonth = parseFloat(row.projected_months_into_project_start || row.actual_months_into_project_start)
        let projectedProjectMonths = parseFloat(row.projected_project_months || row.actual_project_months)
        // TODO uncertain why some are < 0
        if (projectedStartMonth < 0) projectedStartMonth = 1

        let actualStartMonth = parseFloat(row.actual_months_into_project_start)
        let actualProjectMonths = parseFloat(row.actual_project_months)

        let [projectedWidthPct, isProjectedRightAligned] = monthPercent(month, projectedStartMonth, projectedProjectMonths)
        let [actualWidthPct, isActualRightAligned] = monthPercent(month, actualStartMonth, actualProjectMonths)

        months[`month_length_change_${month}`] = [projectedWidthPct, actualWidthPct, isProjectedRightAligned,
          isActualRightAligned, projectedProjectMonths, actualProjectMonths, projectedStartMonth, actualStartMonth]
      })
      dataShow.value.push({
        ...{
          id: row.id,
          project_id: row.project_id,
          dda_term_id: row.dda_term_id,
          hiddenHierarchy: cleanupHierarchyNames(row.itemHierarchy),
          itemHierarchy: cleanupHierarchyNames(row.itemHierarchy),
          projected_project_months: formatDecimals(row.projected_project_months),
          actual_project_months: formatDecimals(row.actual_project_months),
          projected_months_into_project_start: formatDecimals(row.projected_months_into_project_start),
          actual_months_into_project_start: formatDecimals(row.actual_months_into_project_start),
          variance: formatDecimals((row.actual_project_months || 0) - (row.projected_project_months || 0)),
        }, ...months
      })
    }
  });
  isAveragedDataLoaded.value = true
}

let schedStackedBarLabels = ref([]);
let projectedProjectMonthsArr = ref([]);
let actualProjectMonthsArr = ref([]);
let schedVarianceArr = ref([]);
let schedVariancePercArr = ref([]);
let isScheduleOffendersDataLoaded = ref(false)
// Pull schedule data from the grid
function setChartData(event) {
  let chartData = event;

  const HC_GROUP_ID = "row-group-0-HARD COSTS"
  let hcGroupData = chartData.find(row => row.id === HC_GROUP_ID)

  // Create a copy of the leaf children
  const hcGroupDataLeafChildren = hcGroupData?.allLeafChildren ?? [];

  // Sort the values base on variances to get the top offenders in decreasing order
  const sortedHCGroupData = hcGroupDataLeafChildren.sort((a, b) => (b.data.variance!= null ? b.data.variance : 0) 
  - ( a.data.variance!= null ? a.data.variance : 0));


  // Get the top 5 but remove the ones if they include a key that should not be there
  const top5DataHC = [];

  // // Loop through the sorted array for hard cost
  for (const obj of sortedHCGroupData) {
    top5DataHC.push(obj);

    // If top5DataHC array has 5 elements, break the loop
    if (top5DataHC.length === 5) {
      break;
    }
  }

  // build the chart data
  // get the totals first
  let totalProjectedMonths = hcGroupData ? hcGroupData['aggData']['projected_project_months'] : 0
  let totalActualProjectMonths = hcGroupData ? hcGroupData['aggData']['actual_project_months'] : 0
  let totalSchedVariance = totalActualProjectMonths - totalProjectedMonths
  let totalSchedVariancePerc = totalProjectedMonths > 0 ? (totalActualProjectMonths - totalProjectedMonths) / totalProjectedMonths : 0

  schedStackedBarLabels.value = ['Total'];
  projectedProjectMonthsArr.value = [totalProjectedMonths];
  actualProjectMonthsArr.value = [totalActualProjectMonths];
  schedVarianceArr.value = [totalSchedVariance];
  schedVariancePercArr.value = [totalSchedVariancePerc];

  top5DataHC.forEach((row) => {
    let currLabel = row.key
    if (currLabel.length > 12) {
      currLabel = currLabel.slice(0, labelMaxLength) + "...";
    }
    schedStackedBarLabels.value.push(currLabel);
    projectedProjectMonthsArr.value.push(row.data.projected_project_months);
    actualProjectMonthsArr.value.push(row.data.actual_project_months);
    schedVarianceArr.value.push(row.data.actual_project_months - row.data.projected_project_months);
    schedVariancePercArr.value.push((row.data.actual_project_months - row.data.projected_project_months) / row.data.projected_project_months)
  });
  isScheduleOffendersDataLoaded.value = true
}
const apiBase =
  process.env.VUE_APP_ENV_API_BASE || "http://localhost:3001/api/v1/";
const authStore = useAuthStore();
let authToken = reactive({});
// Function to fetch billing-based schedule data
function fetchBillingBasedScheduleData() {
  const selectedIds = Object.keys(compSetStore.selectedProjects).filter(id => compSetStore.selectedProjects[id]).join(',')
  
  if (!selectedIds) return;
  
  // TEMPORARY FIX: Skip API call until backend endpoint is implemented
  console.log("⚠️ Schedule API endpoint not yet implemented, using placeholder data");
  
  // For now, just show that there's no schedule data available
  // This prevents the 404 error while we build out the schedule backend
  setTimeout(() => {
    // Set loading states to indicate no data is available
    isDataLoaded.value = true;
    console.log("📅 Schedule data placeholder loaded - no backend implementation yet");
  }, 100);
  
  // COMMENTED OUT UNTIL BACKEND IS READY:
  // axiosQuery(
  //   `${apiBase}scope_of_works/billing_based_schedule?project_ids=${selectedIds}`,
  //   authToken,
  //   (data) => {
  //     if (data.length === 0) {
  //       console.log("No billing-based schedule data available");
  //     } else {
  //       processBillingBasedScheduleData(data);
  //     }
  //   },
  //   errorStore
  // );
}

// Process the billing-based schedule data
function processBillingBasedScheduleData(data) {
  let processedData = [];
  
  // Group data by hierarchy terms
  let groupedData = {};
  
  data?.forEach((row) => {
    if (row.itemHierarchy) {
      // Process each term in the hierarchy
      cleanupHierarchyNames(row.itemHierarchy).forEach(term => {
        groupedData[term] = groupedData[term] || [];
        
        // Create months data similar to the projected schedule
        let months = {};
        Array(maxMonths).fill(0).forEach((_, month) => {
          // Calculate billing start month (when billing starts)
          let billingStartMonth = row.billing_start_month || 0;
          
          // Calculate 95% completion month (when billing reaches 95%)
          let billing95CompleteMonth = row.billing_95_complete_month || 0;
          
          // Calculate contribution for each month
          let billingContribution = billingStartMonth <= (month + 1) && billing95CompleteMonth >= month + 2 ? 1 : 0;
          
          // Handle partial months
          if (parseFloat(numberFormat(billingStartMonth, 0)) === month) {
            billingContribution = decimalPart(billingStartMonth);
          }
          
          if (parseFloat(numberFormat(billing95CompleteMonth, 0)) === month) {
            billingContribution = decimalPart(billing95CompleteMonth);
          }
          
          // Store the billing contribution data
          months[`month_length_change_${month}`] = [
            0, // No projected data in billing view
            billingContribution, // Billing contribution
            false, // Not right-aligned
            false, // Not right-aligned
            0, // No projected months
            row.actual_project_months || 0, // Actual months
            0, // No projected months in
            row.billing_start_month || 0 // Billing start month
          ];
        });
        
        // Add the row to the grouped data
        groupedData[term].push({
          projected_project_months: row.projected_project_months || 0,
          actual_project_months: row.actual_project_months || 0,
          billing_start_date: row.billing_start_date,
          billing_95_complete_date: row.billing_95_complete_date,
          variance: (row.actual_project_months || 0) - (row.projected_project_months || 0),
          ...months
        });
      });
    }
  });
  
  // Convert grouped data to array format for the grid
  Object.keys(groupedData).forEach(term => {
    let termData = groupedData[term];
    
    // Calculate averages for the term
    let avgProjectedMonths = termData.reduce((sum, item) => sum + item.projected_project_months, 0) / termData.length;
    let avgActualMonths = termData.reduce((sum, item) => sum + item.actual_project_months, 0) / termData.length;
    
    // Get the earliest billing start date and latest 95% complete date
    let billingStartDates = termData.map(item => new Date(item.billing_start_date)).filter(date => !isNaN(date));
    let billing95CompleteDates = termData.map(item => new Date(item.billing_95_complete_date)).filter(date => !isNaN(date));
    
    let earliestBillingStart = billingStartDates.length > 0 ? 
      new Date(Math.min(...billingStartDates)) : null;
    
    let latest95Complete = billing95CompleteDates.length > 0 ? 
      new Date(Math.max(...billing95CompleteDates)) : null;
    
    // Create the month data for the term
    let monthData = {};
    Array(maxMonths).fill(0).forEach((_, month) => {
      // Aggregate the month data from all items in the term
      let monthValues = termData.map(item => item[`month_length_change_${month}`]);
      
      // Calculate the average contribution for the month
      let avgContribution = monthValues.reduce((sum, val) => sum + val[1], 0) / monthValues.length;
      
      monthData[`month_length_change_${month}`] = [
        0, // No projected data in billing view
        avgContribution, // Average billing contribution
        false, // Not right-aligned
        false, // Not right-aligned
        avgProjectedMonths, // Average projected months
        avgActualMonths, // Average actual months
        0, // No projected months in
        0 // No billing start month
      ];
    });
    
    // Add the term row to the processed data
    processedData.push({
      itemHierarchy: term,
      projected_project_months: avgProjectedMonths,
      actual_project_months: avgActualMonths,
      billing_start_date: earliestBillingStart,
      billing_95_complete_date: latest95Complete,
      variance: avgActualMonths - avgProjectedMonths,
      ...monthData
    });
  });
  
  // Update the billing-based schedule data
  billingBasedScheduleData.value = processedData;
}

onMounted(() => {
  authToken.value = authStore.getAuthTokenHeader
  // Enable auth to load fully before updating data (not sure if this completely makes sense)
  setTimeout(() => {
    updateData()
    fetchBillingBasedScheduleData()
  }, 0)
});
function updateData() {
  if (currentProjects.value.length !== 0) {
    fetchGridData()
  } else {
    isNoSelectedProject.value = false
  }

}

function avgAggregate(params) {
  if (!processedAggregateData[params.rowNode.key]) { return null }
  if (params.column.colId === 'projected_project_months') {
    return formatDecimals((processedAggregateData[params.rowNode.key].maxProjectedProjectMonthsLength || 0))
  } else if (params.column.colId === 'actual_project_months') {
    return formatDecimals((processedAggregateData[params.rowNode.key].maxActualProjectMonthsLength || 0))
  } else {
    // Should not get here!
    return null
  }
}
// Uses the sum aggregates so they can be used to calculate variance
// NOTE: this only works because the sum aggregate columns are calculated first!
function varianceAggregate(params) {
  if (!processedAggregateData[params.rowNode.key]) { return null }
  const projected = (processedAggregateData[params.rowNode.key].maxProjectedProjectMonthsLength || 0)
  const actual = (processedAggregateData[params.rowNode.key].maxActualProjectMonthsLength || 0)

  // Not a percent
  return formatDecimals(((actual || 0.0) - (projected || 0.0))) // / (parseFloat(originalBudget) || 1.0))
}

// Aggregate to show in column for this month
// Depending on which data is passed in, the goal is the get:
// 1. minimum months into project start, as we want to see the earliest it is projected to or actually started
// 2. maximum length of project. This needs to be the latest the project could/did end minus the earliest it could/did start
function monthAggregate(params, month) {
  let data = processedAggregateData[params.rowNode.key]

  if (data) {
    let [projectedWidthPct, isProjectedRightAligned] = monthPercent(month, data.minProjectedMonthsIntoProject, data.maxProjectedProjectMonthsLength)
    let [actualWidthPct, isActualRightAligned] = monthPercent(month, data.minActualMonthsIntoProject, data.maxActualProjectMonthsLength)

    return [projectedWidthPct, actualWidthPct, isProjectedRightAligned, isActualRightAligned,
      data.maxProjectedProjectMonthsLength, data.maxActualProjectMonthsLength, data.minProjectedMonthsIntoProject, data.minActualMonthsIntoProject]
  } else {
    return Array(8).fill(0)
  }
}

// Calculates how much of a band to show of a project length
function monthPercent(month, monthsIntoProject, projectMonthLength) {
  let widthPct = 0
  let isRightAligned = false

  if (monthsIntoProject < month + 1) {
    if (monthsIntoProject > month) {
      // spans part of the first column cell
      widthPct = (month + 1) - monthsIntoProject
      isRightAligned = true
    } else if (monthsIntoProject + projectMonthLength > month &&
      monthsIntoProject + projectMonthLength < month + 1) {
      // spans part of the end column cell
      widthPct = (monthsIntoProject + projectMonthLength) - (month)
    } else if (monthsIntoProject <= (month + 2) && monthsIntoProject + projectMonthLength > month) {
      widthPct = 1
    }
  }

  return [widthPct, isRightAligned]
}

const tooltipStore = useGanttTooltipStore()
let tooltipIsShow = ref(tooltipStore.getIsShow)
let tooltipTop = ref(tooltipStore.getTop)
let tooltipLeft = ref(tooltipStore.getLeft)
let tooltipHtml = ref(tooltipStore.getHtml)
tooltipStore.$subscribe(updateTooltip, { detached: false })
function updateTooltip() {
  tooltipIsShow.value = tooltipStore.getIsShow
  tooltipTop.value = tooltipStore.getTop
  tooltipLeft.value = tooltipStore.getLeft
  tooltipHtml.value = tooltipStore.getHtml
}

</script>
<style lang="scss">
@import "./src/assets/main.scss";

.schedule-gantt .schedule-sparkline {
  // keep border, but as narrow as possible
  border-left-width: 0.5px;
  border-right-width: 0.5px;
  outline: 0;
}

.schedule-gantt .schedule-sparkline.year {
  // keep border slightly wider at the year cutoff
  border-left-width: 1.5px;
}

.schedule-gantt .schedule-sparkline-header {
  // keep sparklines flush against cells
  box-sizing: content-box;
  padding-right: 0;
  padding-left: 4px;
}

.schedule-view-toggle {
  display: flex;
  margin-bottom: 15px;
  
  button {
    padding: 8px 16px;
    background-color: #f5f5f5;
    border: 1px solid #ddd;
    cursor: pointer;
    font-size: 14px;
    
    &:first-child {
      border-radius: 4px 0 0 4px;
    }
    
    &:last-child {
      border-radius: 0 4px 4px 0;
    }
    
    &.active {
      background-color: $brightBlue1;
      color: white;
      border-color: $brightBlue1;
    }
    
    &:hover:not(.active) {
      background-color: #e0e0e0;
    }
  }
}

.schedule-tracking-header-container {
  margin-top: 30px;
  margin-bottom: 15px;
  
  .schedule-tracking-header-title {
    font-size: 22px;
    font-weight: bold;
    color: $navyBlue;
    margin-bottom: 5px;
  }
  
  .schedule-tracking-subtitle {
    font-size: 14px;
    color: $paleGray1;
  }
}

.schedule-gantt .schedule-sparkline-header.year {
  // keep border slightly wider at the year cutoff
  border-left-width: 1.5px;
}

.ag-sparkline-tooltip-wrapper .schedule-sparkline-tooltip {
  color: white;
  border: 10px solid $darkBlue1;
  border-radius: 10px;
  background-color: $darkBlue1;
  font-family: $specialFont;
  font-size: 11px;
}
</style>
<style scoped lang="scss">
@import "./src/assets/main.scss";

.schedule-container {
  display: flex;
  flex-direction: column;
  padding: 20px 0;
}

.chart-container {
  flex: 0.5;
}

.grid-container {
  flex: 1;
}

.chart-container {
  display: flex;
  flex-direction: column;
  background: $white1;
  border: 1px solid $white1;
  box-shadow: 6px 0 18px rgba(0, 0, 0, 0.06);
  border-radius: 15px;
  width: 96.5%;
  margin: 0 10px;
}

.chart-title,
.chart-subtitle {
  margin: 10px 15px 15px 15px;
  font-family: $specialFontBold;
}

.chart-title {
  font-size: 16px;
}

.chart-subtitle {
  font-size: 14px;
}

.top-offenders-subtitle {
  visibility: visible;
}

.title-container,
.stacked-charts-container {
  display: flex;
  flex-direction: column;
}

.title-container {
  width: 100%;
}

.stacked-charts-container {
  overflow-x: scroll;
  margin: 0 20px 0 20px;
}

.no-selected-projects {
  padding: 10px;
}
</style>
